import React, { useState, useEffect, useMemo } from 'react';
import './AnalysisDashboard.css';
import { useUserRuns, useRunProgressStream } from '../../../hooks/useUserRuns';
import { transformProgressData, SERVICE_DISPLAY_NAMES, SERVICE_ORDER } from '../../../utils/progressDataTransformer';

const AnalysisDashboard = () => {
    // Get user email from sessionStorage
    const userEmail = sessionStorage.getItem('userEmail') || null;
    
    // Debug logging
    useEffect(() => {
        console.log('📊 AnalysisDashboard mounted');
        console.log('📧 User email:', userEmail || 'NOT FOUND');
        if (!userEmail) {
            console.warn('⚠️ No user email found in sessionStorage. User may need to log in again.');
        }
    }, [userEmail]);
    
    // Load user runs
    const { runs, loading, error, refresh, getRunProgressData, updateRunProgress, runProgress } = useUserRuns(userEmail, {
        autoRefresh: true,
        refreshInterval: 5000, // Refresh every 5 seconds to catch new runs quickly
        connectToActiveStreams: true
    });

    const [selectedRun, setSelectedRun] = useState(null);
    
    // Connect to SSE stream for selected run if it's active
    const isRunActive = selectedRun && (selectedRun.status === 'processing' || selectedRun.status === 'running');
    const { progress: streamProgress, progressData: streamProgressData } = useRunProgressStream(
        selectedRun?.run_id, 
        isRunActive
    );

    // Update progress when stream updates
    useEffect(() => {
        if (streamProgress && selectedRun) {
            updateRunProgress(selectedRun.run_id, streamProgress);
        }
    }, [streamProgress, selectedRun, updateRunProgress]);

    // Get current analysis data (transformed progress data)
    const currentAnalysisData = useMemo(() => {
        if (!selectedRun) return null;
        
        // Debug: Log selectedRun to see what data we have
        console.log('🔍 selectedRun data:', selectedRun);
        console.log('🔍 selectedRun.services:', selectedRun.services);
        console.log('🔍 selectedRun.source:', selectedRun.source);
        
        // Use stream data if available, otherwise use cached progress
        const progress = streamProgress || runProgress[selectedRun.run_id];
        
        // Check if progress has meaningful data (has services with data)
        const hasProgressData = progress && 
            progress.services && 
            typeof progress.services === 'object' && 
            Object.keys(progress.services).length > 0;
        
        if (!hasProgressData) {
            // Only show initial steps if services are available
            // If Redis was cleared and this is a new run (< 20 hours), services will be empty
            // In that case, don't show anything (as per user requirement)
            if (!selectedRun.services || selectedRun.services.length === 0) {
                // Check if this is from MongoDB (old task > 20 hours) - if so, we should still show it
                const isOldTask = selectedRun.source === 'mongodb' || selectedRun.data_source === 'mongodb';
                if (!isOldTask) {
                    // New task without services (Redis was cleared) - don't show anything
                    console.warn('⚠️ No services found for run (Redis likely cleared):', selectedRun.run_id);
                    return {
                        id: selectedRun.run_id,
                        project_id: selectedRun.project_id,
                        project_name: selectedRun.name,
                        status: selectedRun.status || 'pending',
                        progress: {
                            percentage: 0,
                            completed_steps: 0,
                            total_steps: 0
                        },
                        steps: [] // Empty steps - will show "No services configured"
                    };
                }
            }
            
            // Create initial steps from selectedRun.services if available
            // This ensures progress circles appear immediately after launch
            console.log('📊 No progress data yet, creating initial steps from services:', selectedRun.services);
            const getServiceDescription = (name) => {
                const descriptions = {
                    'tiling': 'Breaking large LiDAR files into manageable tiles for processing',
                    'csf': 'Classifying ground points using CSF algorithm',
                    'car_removal': 'Removing vehicles and other moving objects from the point cloud',
                    'road_detection': 'Detecting road boundaries and centerlines',
                    'embankment': 'Analyzing road embankments and slopes',
                    'horizontal': 'Calculating horizontal curves and alignments',
                    'slope_calc': 'Calculating slope values and gradients',
                    'psd_classification': 'Classifying PSD (Particle Size Distribution) data',
                    'automatic_report': 'Generating automatic analysis reports',
                    'report_generation': 'Generating final analysis report with visualizations'
                };
                return descriptions[name] || `Processing ${name}`;
            };
            
            // Sort services by execution order
            const sortedServices = [...(selectedRun.services || [])].sort((a, b) => {
                const indexA = SERVICE_ORDER.indexOf(a);
                const indexB = SERVICE_ORDER.indexOf(b);
                if (indexA === -1 && indexB === -1) return 0;
                if (indexA === -1) return 1;
                if (indexB === -1) return -1;
                return indexA - indexB;
            });
            
            const initialSteps = sortedServices.map((serviceName, index) => {
                const isFirst = index === 0;
                
                return {
                    name: serviceName,
                    display_name: SERVICE_DISPLAY_NAMES[serviceName] || serviceName,
                    status: isFirst ? 'running' : 'pending', // First step is running to show orange glow
                    progress: isFirst ? 0 : 0, // First step starts at 0% but is "running"
                    message: isFirst ? 'Initializing...' : 'Waiting...',
                    started_at: null,
                    completed_at: null,
                    duration: null,
                    description: getServiceDescription(serviceName),
                    instance_id: null,
                    folder_id: null,
                    folder_name: null
                };
            });
            
            // If no services found, log warning but still return structure
            if (initialSteps.length === 0) {
                console.warn('⚠️ No services found for run:', selectedRun.run_id, 'Run data:', selectedRun);
            }
            
            // Return basic structure with initial steps
            return {
                id: selectedRun.run_id,
                project_id: selectedRun.project_id,
                project_name: selectedRun.project_name || selectedRun.name,  // Use project_name if available
                status: selectedRun.status || 'pending',
                progress: {
                    percentage: selectedRun.overall_progress || 0,
                    completed_steps: 0,
                    total_steps: initialSteps.length || 0
                },
                steps: initialSteps
            };
        }
        
        const transformed = transformProgressData(progress, {
            run_id: selectedRun.run_id,
            project_id: selectedRun.project_id,
            project_name: selectedRun.project_name || selectedRun.name,  // Use project_name if available
            created_at: selectedRun.created_at,
            user_email: selectedRun.user_email,
            input_files: []
        });
        
        // If transformed data has no steps but we have services, merge with initial steps
        if ((!transformed.steps || transformed.steps.length === 0) && 
            selectedRun.services && 
            selectedRun.services.length > 0) {
            console.log('⚠️ Progress data has no steps, falling back to initial steps from services');
            const getServiceDescription = (name) => {
                const descriptions = {
                    'tiling': 'Breaking large LiDAR files into manageable tiles for processing',
                    'csf': 'Classifying ground points using CSF algorithm',
                    'car_removal': 'Removing vehicles and other moving objects from the point cloud',
                    'road_detection': 'Detecting road boundaries and centerlines',
                    'embankment': 'Analyzing road embankments and slopes',
                    'horizontal': 'Calculating horizontal curves and alignments',
                    'slope_calc': 'Calculating slope values and gradients',
                    'psd_classification': 'Classifying PSD (Particle Size Distribution) data',
                    'automatic_report': 'Generating automatic analysis reports',
                    'report_generation': 'Generating final analysis report with visualizations'
                };
                return descriptions[name] || `Processing ${name}`;
            };
            
            const sortedServices = [...(selectedRun.services || [])].sort((a, b) => {
                const indexA = SERVICE_ORDER.indexOf(a);
                const indexB = SERVICE_ORDER.indexOf(b);
                if (indexA === -1 && indexB === -1) return 0;
                if (indexA === -1) return 1;
                if (indexB === -1) return -1;
                return indexA - indexB;
            });
            
            transformed.steps = sortedServices.map((serviceName, index) => {
                const isFirst = index === 0;
                return {
                    name: serviceName,
                    display_name: SERVICE_DISPLAY_NAMES[serviceName] || serviceName,
                    status: isFirst ? 'running' : 'pending', // First step is running to show orange glow
                    progress: 0,
                    message: isFirst ? 'Initializing...' : 'Waiting...',
                    started_at: null,
                    completed_at: null,
                    duration: null,
                    description: getServiceDescription(serviceName),
                    instance_id: null,
                    folder_id: null,
                    folder_name: null
                };
            });
            
            transformed.progress = {
                ...transformed.progress,
                total_steps: transformed.steps.length
            };
        }
        
        return transformed;
    }, [selectedRun, streamProgress, runProgress]);

    // Auto-select first run on load
    useEffect(() => {
        if (runs.length > 0 && !selectedRun) {
            setSelectedRun(runs[0]);
        }
    }, [runs, selectedRun]);

    // Removed mock data - using real data from hooks
    const mockAnalysesData = [ // Keep for reference but not used
        {
            id: 'analysis_123',
            project_name: 'Highway LiDAR Analysis',
            project_id: 'project_789',
            status: 'running',
            start_time: new Date(Date.now() - 2 * 60 * 60 * 1000), // 2 hours ago
            estimated_completion: new Date(Date.now() + 1 * 60 * 60 * 1000), // 1 hour from now
            progress: {
                percentage: 65,
                completed_steps: 3,
                total_steps: 6
            },
            steps: [
                {
                    name: 'tiling',
                    display_name: 'File Tiling',
                    status: 'completed',
                    progress: 100,
                    started_at: new Date(Date.now() - 2 * 60 * 60 * 1000),
                    completed_at: new Date(Date.now() - 1.5 * 60 * 60 * 1000),
                    duration: 1800, // 30 minutes
                    description: 'Breaking large LiDAR files into manageable tiles for processing'
                },
                {
                    name: 'csf',
                    display_name: 'Ground Classification',
                    status: 'completed',
                    progress: 100,
                    started_at: new Date(Date.now() - 1.5 * 60 * 60 * 1000),
                    completed_at: new Date(Date.now() - 1 * 60 * 60 * 1000),
                    duration: 1800, // 30 minutes
                    description: 'Classifying ground points using CSF algorithm'
                },
                {
                    name: 'car_removal',
                    display_name: 'Vehicle Removal',
                    status: 'running',
                    progress: 75,
                    started_at: new Date(Date.now() - 1 * 60 * 60 * 1000),
                    completed_at: null,
                    duration: null,
                    description: 'Removing vehicles and other moving objects from the point cloud'
                },
                {
                    name: 'road_detection',
                    display_name: 'Road Detection',
                    status: 'pending',
                    progress: 0,
                    started_at: null,
                    completed_at: null,
                    duration: null,
                    description: 'Detecting road boundaries and centerlines'
                },
                {
                    name: 'embankment',
                    display_name: 'Embankment Analysis',
                    status: 'pending',
                    progress: 0,
                    started_at: null,
                    completed_at: null,
                    duration: null,
                    description: 'Analyzing road embankments and slopes'
                },
                {
                    name: 'report_generation',
                    display_name: 'Report Generation',
                    status: 'pending',
                    progress: 0,
                    started_at: null,
                    completed_at: null,
                    duration: null,
                    description: 'Generating final analysis report with visualizations'
                }
            ],
            input_files: ['highway_section_1.las', 'highway_section_2.las'],
            user_id: 'user_456',
            project_id: 'project_789'
        },
        {
            id: 'analysis_456',
            project_name: 'Urban Road Analysis',
            project_id: 'project_789',
            status: 'completed',
            start_time: new Date(Date.now() - 4 * 60 * 60 * 1000),
            completed_at: new Date(Date.now() - 1 * 60 * 60 * 1000),
            progress: {
                percentage: 100,
                completed_steps: 6,
                total_steps: 6
            },
            steps: [
                {
                    name: 'tiling',
                    display_name: 'File Tiling',
                    status: 'completed',
                    progress: 100,
                    started_at: new Date(Date.now() - 4 * 60 * 60 * 1000),
                    completed_at: new Date(Date.now() - 3.5 * 60 * 60 * 1000),
                    duration: 1800,
                    description: 'Breaking large LiDAR files into manageable tiles for processing'
                },
                {
                    name: 'csf',
                    display_name: 'Ground Classification',
                    status: 'completed',
                    progress: 100,
                    started_at: new Date(Date.now() - 3.5 * 60 * 60 * 1000),
                    completed_at: new Date(Date.now() - 3 * 60 * 60 * 1000),
                    duration: 1800,
                    description: 'Classifying ground points using CSF algorithm'
                },
                {
                    name: 'car_removal',
                    display_name: 'Vehicle Removal',
                    status: 'completed',
                    progress: 100,
                    started_at: new Date(Date.now() - 3 * 60 * 60 * 1000),
                    completed_at: new Date(Date.now() - 2.5 * 60 * 60 * 1000),
                    duration: 1800,
                    description: 'Removing vehicles and other moving objects from the point cloud'
                },
                {
                    name: 'road_detection',
                    display_name: 'Road Detection',
                    status: 'completed',
                    progress: 100,
                    started_at: new Date(Date.now() - 2.5 * 60 * 60 * 1000),
                    completed_at: new Date(Date.now() - 2 * 60 * 60 * 1000),
                    duration: 1800,
                    description: 'Detecting road boundaries and centerlines'
                },
                {
                    name: 'embankment',
                    display_name: 'Embankment Analysis',
                    status: 'completed',
                    progress: 100,
                    started_at: new Date(Date.now() - 2 * 60 * 60 * 1000),
                    completed_at: new Date(Date.now() - 1.5 * 60 * 60 * 1000),
                    duration: 1800,
                    description: 'Analyzing road embankments and slopes'
                },
                {
                    name: 'report_generation',
                    display_name: 'Report Generation',
                    status: 'completed',
                    progress: 100,
                    started_at: new Date(Date.now() - 1.5 * 60 * 60 * 1000),
                    completed_at: new Date(Date.now() - 1 * 60 * 60 * 1000),
                    duration: 1800,
                    description: 'Generating final analysis report with visualizations'
                }
            ],
            input_files: ['urban_road_1.las', 'urban_road_2.las'],
            user_id: 'user_456',
            project_id: 'project_789'
        },
        {
            id: 'analysis_789',
            project_name: 'Bridge Analysis Project',
            project_id: 'project_452',
            status: 'running',
            start_time: new Date(Date.now() - 30 * 60 * 1000), // 30 minutes ago
            estimated_completion: new Date(Date.now() + 3 * 60 * 60 * 1000), // 3 hours from now
            progress: {
                percentage: 25,
                completed_steps: 1,
                total_steps: 6
            },
            steps: [
                {
                    name: 'tiling',
                    display_name: 'File Tiling',
                    status: 'completed',
                    progress: 100,
                    started_at: new Date(Date.now() - 30 * 60 * 1000),
                    completed_at: new Date(Date.now() - 25 * 60 * 1000),
                    duration: 300, // 5 minutes
                    description: 'Breaking large LiDAR files into manageable tiles for processing'
                },
                {
                    name: 'csf',
                    display_name: 'Ground Classification',
                    status: 'running',
                    progress: 50,
                    started_at: new Date(Date.now() - 25 * 60 * 1000),
                    completed_at: null,
                    duration: null,
                    description: 'Classifying ground points using CSF algorithm'
                },
                {
                    name: 'car_removal',
                    display_name: 'Vehicle Removal',
                    status: 'pending',
                    progress: 0,
                    started_at: null,
                    completed_at: null,
                    duration: null,
                    description: 'Removing vehicles and other moving objects from the point cloud'
                },
                {
                    name: 'road_detection',
                    display_name: 'Road Detection',
                    status: 'pending',
                    progress: 0,
                    started_at: null,
                    completed_at: null,
                    duration: null,
                    description: 'Detecting road boundaries and centerlines'
                },
                {
                    name: 'embankment',
                    display_name: 'Embankment Analysis',
                    status: 'pending',
                    progress: 0,
                    started_at: null,
                    completed_at: null,
                    duration: null,
                    description: 'Analyzing road embankments and slopes'
                },
                {
                    name: 'report_generation',
                    display_name: 'Report Generation',
                    status: 'pending',
                    progress: 0,
                    started_at: null,
                    completed_at: null,
                    duration: null,
                    description: 'Generating final analysis report with visualizations'
                }
            ],
            input_files: ['bridge_section.las'],
            user_id: 'user_456',
            project_id: 'project_452'
        },
        {
            id: 'analysis_486',
            project_name: 'West Kingman Interchange Analysis',
            project_id: 'project_486',
            status: 'pending',
            start_time: new Date(Date.now() - 15 * 60 * 1000), // 15 minutes ago
            estimated_completion: new Date(Date.now() + 4 * 60 * 60 * 1000), // 4 hours from now
            progress: {
                percentage: 0,
                completed_steps: 0,
                total_steps: 6
            },
            steps: [
                {
                    name: 'tiling',
                    display_name: 'File Tiling',
                    status: 'pending',
                    progress: 0,
                    started_at: null,
                    completed_at: null,
                    duration: null,
                    description: 'Breaking large LiDAR files into manageable tiles for processing'
                },
                {
                    name: 'csf',
                    display_name: 'Ground Classification',
                    status: 'pending',
                    progress: 0,
                    started_at: null,
                    completed_at: null,
                    duration: null,
                    description: 'Classifying ground points using CSF algorithm'
                },
                {
                    name: 'car_removal',
                    display_name: 'Vehicle Removal',
                    status: 'pending',
                    progress: 0,
                    started_at: null,
                    completed_at: null,
                    duration: null,
                    description: 'Removing vehicles and other moving objects from the point cloud'
                },
                {
                    name: 'road_detection',
                    display_name: 'Road Detection',
                    status: 'pending',
                    progress: 0,
                    started_at: null,
                    completed_at: null,
                    duration: null,
                    description: 'Detecting road boundaries and centerlines'
                },
                {
                    name: 'embankment',
                    display_name: 'Embankment Analysis',
                    status: 'pending',
                    progress: 0,
                    started_at: null,
                    completed_at: null,
                    duration: null,
                    description: 'Analyzing road embankments and slopes'
                },
                {
                    name: 'report_generation',
                    display_name: 'Report Generation',
                    status: 'pending',
                    progress: 0,
                    started_at: null,
                    completed_at: null,
                    duration: null,
                    description: 'Generating final analysis report with visualizations'
                }
            ],
            input_files: ['west_kingman.las'],
            user_id: 'user_456',
            project_id: 'project_486'
        },
        {
            id: 'analysis_416',
            project_name: 'SR 516 Lake-Orange County Analysis',
            project_id: 'project_416',
            status: 'completed',
            start_time: new Date(Date.now() - 6 * 60 * 60 * 1000), // 6 hours ago
            completed_at: new Date(Date.now() - 2 * 60 * 60 * 1000), // 2 hours ago
            progress: {
                percentage: 100,
                completed_steps: 6,
                total_steps: 6
            },
            steps: [
                {
                    name: 'tiling',
                    display_name: 'File Tiling',
                    status: 'completed',
                    progress: 100,
                    started_at: new Date(Date.now() - 6 * 60 * 60 * 1000),
                    completed_at: new Date(Date.now() - 5.5 * 60 * 60 * 1000),
                    duration: 1800, // 30 minutes
                    description: 'Breaking large LiDAR files into manageable tiles for processing'
                },
                {
                    name: 'csf',
                    display_name: 'Ground Classification',
                    status: 'completed',
                    progress: 100,
                    started_at: new Date(Date.now() - 5.5 * 60 * 60 * 1000),
                    completed_at: new Date(Date.now() - 5 * 60 * 60 * 1000),
                    duration: 1800, // 30 minutes
                    description: 'Classifying ground points using CSF algorithm'
                },
                {
                    name: 'car_removal',
                    display_name: 'Vehicle Removal',
                    status: 'completed',
                    progress: 100,
                    started_at: new Date(Date.now() - 5 * 60 * 60 * 1000),
                    completed_at: new Date(Date.now() - 4.5 * 60 * 60 * 1000),
                    duration: 1800, // 30 minutes
                    description: 'Removing vehicles and other moving objects from the point cloud'
                },
                {
                    name: 'road_detection',
                    display_name: 'Road Detection',
                    status: 'completed',
                    progress: 100,
                    started_at: new Date(Date.now() - 4.5 * 60 * 60 * 1000),
                    completed_at: new Date(Date.now() - 4 * 60 * 60 * 1000),
                    duration: 1800, // 30 minutes
                    description: 'Detecting road boundaries and centerlines'
                },
                {
                    name: 'embankment',
                    display_name: 'Embankment Analysis',
                    status: 'completed',
                    progress: 100,
                    started_at: new Date(Date.now() - 4 * 60 * 60 * 1000),
                    completed_at: new Date(Date.now() - 3.5 * 60 * 60 * 1000),
                    duration: 1800, // 30 minutes
                    description: 'Analyzing road embankments and slopes'
                },
                {
                    name: 'report_generation',
                    display_name: 'Report Generation',
                    status: 'completed',
                    progress: 100,
                    started_at: new Date(Date.now() - 3.5 * 60 * 60 * 1000),
                    completed_at: new Date(Date.now() - 2 * 60 * 60 * 1000),
                    duration: 1800, // 30 minutes
                    description: 'Generating final analysis report with visualizations'
                }
            ],
            input_files: ['sr_516_lake_orange.las'],
            user_id: 'user_456',
            project_id: 'project_416'
        }
    ];

    // Mock projects data
    const mockProjectsData = [
        {
            id: 'project_789',
            name: 'CFX SR-538 Poinciana Pkwy',
            description: 'Comprehensive analysis of highway sections for maintenance planning',
            total_analyses: 2,
            completed_analyses: 1,
            running_analyses: 1,
            last_updated: new Date(Date.now() - 30 * 60 * 1000)
        },
        {
            id: 'project_452',
            name: 'ADOT Climbing Passing RSA',
            description: 'Structural analysis of bridge components using LiDAR data',
            total_analyses: 1,
            completed_analyses: 0,
            running_analyses: 1,
            last_updated: new Date(Date.now() - 30 * 60 * 1000)
        },
        {
            id: 'project_486',
            name: 'ADOT West Kingman Interchange',
            description: 'Structural analysis of bridge components using LiDAR data',
            total_analyses: 1,
            completed_analyses: 0,
            running_analyses: 0,
            last_updated: new Date(Date.now() - 30 * 60 * 1000)
        },
        {
            id: 'project_416',
            name: 'CFX SR 516 Lake-Orange County',
            description: 'Structural analysis of bridge components using LiDAR data',
            total_analyses: 1,
            completed_analyses: 1,
            running_analyses: 0,
            last_updated: new Date(Date.now() - 30 * 60 * 1000)
        },
    ];

    // No need for useEffect - data loaded via hooks



    const getStatusClass = (status) => {
        switch (status) {
            case 'completed':
                return 'status-completed';
            case 'running':
                return 'status-running';
            case 'failed':
                return 'status-failed';
            case 'pending':
                return 'status-pending';
            default:
                return 'status-pending';
        }
    };

    const formatTime = (date) => {
        return date.toLocaleTimeString('en-US', { 
            hour: '2-digit', 
            minute: '2-digit',
            hour12: true 
        });
    };

    const formatDuration = (seconds) => {
        const hours = Math.floor(seconds / 3600);
        const minutes = Math.floor((seconds % 3600) / 60);
        if (hours > 0) {
            return `${hours}h ${minutes}m`;
        }
        return `${minutes}m`;
    };

    const formatDate = (date) => {
        const now = new Date();
        const diffInMinutes = Math.floor((now - date) / (1000 * 60));
        
        if (diffInMinutes < 1) return 'Just now';
        if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
        
        const diffInHours = Math.floor(diffInMinutes / 60);
        if (diffInHours < 24) return `${diffInHours}h ago`;
        
        const diffInDays = Math.floor(diffInHours / 24);
        if (diffInDays < 7) return `${diffInDays}d ago`;
        
        return date.toLocaleDateString('en-US', { 
            month: 'short', 
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };



    if (loading) {
        return (
            <div className="dashboard-page">
                <div className="dashboard-loading">
                    <div className="analysis-dashboard-loading-spinner"></div>
                    <p>Loading analysis dashboard...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="dashboard-page">
                <div className="dashboard-error">
                    <p>{error}</p>
                    <button onClick={() => window.location.reload()}>Retry</button>
                </div>
            </div>
        );
    }

    return (
        <div className="dashboard-page">
            <div className="dashboard-layout">
                {/* Main Content Area */}
                <div className="main-content-area-dashboard">
                    {/* Analysis List */}
                    <div className="analyses-list">
                        {!selectedRun ? (
                            <div className="no-analyses">
                                <p>Select a run from the list to view progress details.</p>
                            </div>
                        ) : !currentAnalysisData ? (
                            <div className="no-analyses">
                                <p>Loading progress data...</p>
                            </div>
                        ) : (
                            <div className="analyses-container">
                                {/* Analysis Card based on Run Status */}
                                <div className={`analysis-card ${getRunStatusClass(selectedRun)}`}>
                                    {/* Pagination Info at Top of Card */}
                          

                                                                         {/* Top Section - Progress Circles */}
                                     <div className="progress-circles-section">
                                         <div className="progress-circles-container">
                                             {currentAnalysisData.steps && currentAnalysisData.steps.length > 0 ? (
                                                 currentAnalysisData.steps.map((step, index) => (
                                                     <div key={index} className="progress-step">
                                                         <div className={`progress-circle ${getStatusClass(step.status)}`}>
                                                             {step.status === 'completed' ? (
                                                                 <span className="progress-checkmark">✓</span>
                                                             ) : (
                                                                 <span className="circle-number">{index + 1}</span>
                                                             )}
                                                         </div>
                                                         <div className="step-label" dangerouslySetInnerHTML={{ 
                                                             __html: step.display_name === 'File Tiling' ? 'File<br>Tiling' : step.display_name 
                                                         }}></div>

                                                     </div>
                                                 ))
                                             ) : (
                                                 <div className="no-steps">
                                                     <p>No services configured for this run.</p>
                                                 </div>
                                             )}
                                         </div>
                                     </div>

                                                                         {/* Bottom Section - Active Step Details */}
                                     <div className="active-step-section">
                                         {(() => {
                                             if (!currentAnalysisData || !currentAnalysisData.steps || currentAnalysisData.steps.length === 0) {
                                                 return (
                                                     <div className="active-step-content">
                                                         <div className="active-step-header">
                                                             <h2>No Active Step</h2>
                                                         </div>
                                                         <div className="active-step-description">
                                                             <p>Waiting for pipeline to start...</p>
                                                         </div>
                                                     </div>
                                                 );
                                             }
                                             
                                             // Find active step: running > pending (first one) > last completed
                                            const activeStep = currentAnalysisData.steps.find(step => step.status === 'running') || 
                                                              currentAnalysisData.steps.find(step => step.status === 'pending') ||
                                                              currentAnalysisData.steps.find(step => step.status === 'processing') ||
                                                              currentAnalysisData.steps[0] || // Fallback to first step
                                                              currentAnalysisData.steps[currentAnalysisData.steps.length - 1];
                                            
                                            return (
                                                <div className="active-step-content">
                                                    <div className="active-step-header">
                                                        <h2>{activeStep.display_name}</h2>
                                                        <div className={`active-step-status ${getStatusClass(activeStep.status)}`}>
                                                            {activeStep.status.toUpperCase()}
                                                        </div>
                                                    </div>
                                                    
                                                    <div className="active-step-description">
                                                        <p>{activeStep.description}</p>
                                                    </div>

                                                    {activeStep.status === 'running' && (
                                                        <div className="active-step-progress">
                                                            <div className="step-progress-header">
                                                                <span>Progress</span>
                                                                <span className="step-progress-percentage">{activeStep.progress}%</span>
                                                            </div>
                                                            <div className="step-progress-bar">
                                                                <div 
                                                                    className="step-progress-fill"
                                                                    style={{ width: `${activeStep.progress}%` }}
                                                                ></div>
                                                            </div>
                                                        </div>
                                                    )}

                                                    <div className="active-step-times">
                                                        {activeStep.started_at && (
                                                            <div className="time-info">
                                                                <span className="time-label">Started:</span>
                                                                <span className="time-value">{formatTime(activeStep.started_at)}</span>
                                                            </div>
                                                        )}
                                                        {activeStep.completed_at && (
                                                            <div className="time-info">
                                                                <span className="time-label">Completed:</span>
                                                                <span className="time-value">{formatTime(activeStep.completed_at)}</span>
                                                            </div>
                                                        )}
                                                        {activeStep.duration && (
                                                            <div className="time-info">
                                                                <span className="time-label">Duration:</span>
                                                                <span className="time-value">{formatDuration(activeStep.duration)}</span>
                                                            </div>
                                                        )}
                                                    </div>

                                                                                                         {/* Analysis Meta Info */}
                                                     <div className="analysis-meta-info">
                                                         <div className="meta-item">
                                                             <span className="meta-label">Project:</span>
                                                             <span className="meta-value">{selectedRun.project_name || currentAnalysisData.project_name || selectedRun.project_id || 'Unknown'}</span>
                                                         </div>
                                                        <div className="meta-item">
                                                            <span className="meta-label">Overall Progress:</span>
                                                            <span className="meta-value">
                                                                {Math.round(
                                                                    currentAnalysisData.progress?.percentage ?? 
                                                                    selectedRun.overall_progress ?? 0
                                                                )}%
                                                            </span>
                                                        </div>
                                                         <div className="meta-item">
                                                             <span className="meta-label">Steps Completed:</span>
                                                             <span className="meta-value">{currentAnalysisData.progress?.completed_steps || 0} of {currentAnalysisData.progress?.total_steps || 0}</span>
                                                         </div>
                                                         {selectedRun.run_id && (
                                                             <div className="meta-item">
                                                                 <span className="meta-label">Run ID:</span>
                                                                 <span className="meta-value" style={{ fontSize: '0.8rem', fontFamily: 'monospace' }}>{selectedRun.run_id}</span>
                                                             </div>
                                                         )}
                                                     </div>

                                                                                                         {/* Action Buttons */}
                                                     <div className="analysis-actions">
                                                         {currentAnalysisData.status === 'completed' && (
                                                             <>
                                                                 <button className="action-button primary">View Report</button>
                                                                 <button className="action-button secondary">Download Report</button>
                                                             </>
                                                         )}
                                                         
                                                         {(currentAnalysisData.status === 'running' || currentAnalysisData.status === 'processing') && (
                                                             <button className="action-button warning">Cancel Analysis</button>
                                                         )}
                                                         
                                                         <button className="action-button secondary" onClick={refresh} style={{ marginLeft: '10px' }}>
                                                             Refresh
                                                         </button>
                                                     </div>
                                                </div>
                                            );
                                        })()}
                                    </div>
                                </div>

                                                             </div>
                        )}
                    </div>
                </div>

                {/* Right Side Panel - Projects List */}
                <div className="right-panel">
                    <div className="projects-panel">
                        <div className="analysis-dashboard-panel-header">
                            <h3>Runs</h3>
                            <span className="project-count">{runs.length} {runs.length === 1 ? 'Run' : 'Runs'}</span>
                        </div>
                        
                        <div className="projects-list">
                            {runs.length === 0 && !loading ? (
                                <div className="no-runs">
                                    <p>No pipeline runs found. Start a new analysis from the RSA Analysis page.</p>
                                </div>
                            ) : (
                                runs.map((run, index) => (
                                    <div 
                                        key={`${run.run_id}-${index}`} 
                                        className={`project-item ${selectedRun?.run_id === run.run_id ? 'selected' : ''} ${getRunStatusClass(run)}`}
                                        onClick={() => setSelectedRun(run)}
                                    >
                                        <div className="analysis-dashboard-project-header">
                                            <h4>{run.name}</h4>
                                            <div className={`project-status ${getRunStatusClass(run)}`}>
                                                {getRunStatusText(run)}
                                            </div>
                                        </div>
                                        
                                        <p className="project-description">{run.description}</p>
                                        
                                        <div className="project-stats">
                                            <div className="stat-item">
                                                <span className="stat-label">Progress:</span>
                                                <span className="stat-value">{Math.round(run.overall_progress || 0)}%</span>
                                            </div>
                                            <div className="stat-item">
                                                <span className="stat-label">Status:</span>
                                                <span className={`stat-value ${run.status === 'completed' ? 'completed' : run.status === 'processing' ? 'running' : ''}`}>
                                                    {run.status || 'pending'}
                                                </span>
                                            </div>
                                            {run.project_id && (
                                                <div className="stat-item">
                                                    <span className="stat-label">Project:</span>
                                                    <span className="stat-value">{run.project_id}</span>
                                                </div>
                                            )}
                                        </div>
                                        
                                        <div className="project-footer">
                                            <span className="last-updated">Updated: {formatDate(run.last_updated)}</span>
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );

    // Helper functions for run status
    function getRunStatusClass(run) {
        if (run.status === 'processing' || run.status === 'running') return 'running';
        if (run.status === 'completed') return 'completed';
        if (run.status === 'failed') return 'failed';
        return 'pending';
    }

    function getRunStatusText(run) {
        if (run.status === 'processing' || run.status === 'running') return 'Running';
        if (run.status === 'completed') return 'Completed';
        if (run.status === 'failed') return 'Failed';
        return 'Pending';
    }
};

export default AnalysisDashboard;
